/**
 * Which navigation item the current URL belongs to.
 *
 * Three kinds of item share one menu and each matches differently, which is
 * why this is worth its own function rather than a condition inside the
 * component:
 *
 *   a tab          `/x?tab=posts`     — matches only that tab
 *   the first tab  `/x?tab=calendar`  — also matches `/x` with NO tab, since
 *                                       that is what the page shows by default
 *   a section      `/x`               — matches the path and everything under it
 *
 * Every tab carries its `?tab=`, the first one included, so a tab has exactly
 * one address. The page still opens on its first tab when the query is absent,
 * and the caller says which child that is with `defaultTab`.
 */

export interface ActiveQuery {
    /** The item's route, possibly carrying `?tab=`. */
    route: string;
    /** Other paths that should light this item, for a section with sub-pages. */
    activeRoutes?: string[];
    /** The current `location.pathname`. */
    pathname: string;
    /** The current `location.search`. */
    search: string;
    /** True when this item is one tab among siblings that use `?tab=`. */
    amongTabs?: boolean;
    /** True for the tab a page shows when the URL names none. */
    defaultTab?: boolean;
}

const tabOf = (search: string): string => new URLSearchParams(search).get('tab') || '';

export function isRouteActive({
    route,
    activeRoutes,
    pathname,
    search,
    amongTabs = false,
    defaultTab = false,
}: ActiveQuery): boolean {
    const [path, query = ''] = route.split('?');

    // A route carrying ?tab= matches only its own tab, so sibling tabs do not
    // all light up at once.
    // A row can also stand for more tabs than its own (activeRoutes).
    if (query) {
        if (pathname !== path) return false;
        const current = tabOf(search);
        if (!current && defaultTab) return true;
        const tabs = [route, ...(activeRoutes || [])]
            .filter((r) => r.split('?')[0] === path)
            .map((r) => new URLSearchParams(r.split('?')[1] || '').get('tab') || '');
        return tabs.includes(current);
    }

    // The tab-less sibling is a tab too: this path AND no tab.
    if (amongTabs) {
        return pathname === path && !tabOf(search);
    }

    // A section: the path itself, or anything beneath it.
    const paths = activeRoutes?.length ? activeRoutes : [path];
    return paths.some((r) => {
        const base = r.split('?')[0];
        return pathname === base || pathname.startsWith(`${base}/`);
    });
}
